import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

function useScrollReveal() {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll<HTMLElement>(
      ".lp-reveal, .lp-reveal-left, .lp-reveal-right",
    );
    if (!targets.length) return;

    function reveal() {
      const windowBottom = window.scrollY + window.innerHeight - 60;
      targets.forEach((el) => {
        if (el.classList.contains("is-visible")) return;
        const elTop = el.getBoundingClientRect().top + window.scrollY;
        if (windowBottom > elTop) el.classList.add("is-visible");
      });
    }

    let ticking = false;
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          reveal();
          ticking = false;
        });
        ticking = true;
      }
    }

    reveal();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return rootRef;
}

export function LandingPage() {
  const rootRef = useScrollReveal();

  useEffect(() => {
    document.body.classList.add("body-landing");
    return () => document.body.classList.remove("body-landing");
  }, []);

  function handleAnchorClick(e: React.MouseEvent<HTMLAnchorElement>) {
    const href = e.currentTarget.getAttribute("href");
    if (!href || !href.startsWith("#")) return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div ref={rootRef}>
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <Link className="lp-nav-brand" to="/">
            <img
              className="brand-mark"
              src="/logo.png"
              alt=""
              width={40}
              height={40}
              draggable={false}
            />
            <span className="lp-nav-brand-text">
              <strong>Flight Analytics</strong>
              <small>Drone pilot training</small>
            </span>
          </Link>
          <div className="lp-nav-links">
            <a href="#what-you-get" onClick={handleAnchorClick}>
              What you get
            </a>
            <a href="#how-it-works" onClick={handleAnchorClick}>
              How it works
            </a>
            <a href="#who-its-for" onClick={handleAnchorClick}>
              Who it's for
            </a>
          </div>
          <div className="lp-nav-actions">
            <Link className="button ghost" to="/login">
              Sign in
            </Link>
            <Link className="button primary" to="/register">
              Try it out
            </Link>
          </div>
          <button
            className="lp-nav-mobile"
            type="button"
            onClick={() => {
              document.querySelector(".lp-nav-links")?.classList.toggle("open");
              document
                .querySelector(".lp-nav-actions")
                ?.classList.toggle("open");
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
        </div>
      </nav>

      <section className="lp-hero">
        <div className="lp-hero-glow" />
        <div className="lp-hero-content">
          <p className="lp-hero-kicker">For drone pilots & instructors</p>
          <h1>
            Your flight just ended.
            <br />
            The debrief starts here.
          </h1>
          <p className="lp-hero-text">
            Upload a replay, and within seconds you'll see exactly what
            happened — 3D playback, telemetry charts, AI feedback, and a
            smoothness score. No more guessing what went wrong at turn three.
          </p>
          <div className="lp-hero-actions">
            <Link
              className="button primary lp-btn-lg"
              to="/register?next=/flights/upload"
            >
              Start with registration
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="18"
                height="18"
              >
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </Link>
            <a
              className="button ghost lp-btn-lg"
              href="#what-you-get"
              onClick={handleAnchorClick}
            >
              See what's inside
            </a>
          </div>
          <div className="lp-hero-stats">
            <div className="lp-hero-stat">
              <span className="lp-hero-stat-val">3D</span>
              <span className="lp-hero-stat-lbl">Flight replay</span>
            </div>
            <div className="lp-hero-stat">
              <span className="lp-hero-stat-val">AI</span>
              <span className="lp-hero-stat-lbl">Flight coaching</span>
            </div>
            <div className="lp-hero-stat">
              <span className="lp-hero-stat-val">7+</span>
              <span className="lp-hero-stat-lbl">Telemetry channels</span>
            </div>
            <div className="lp-hero-stat">
              <span className="lp-hero-stat-val">A/B</span>
              <span className="lp-hero-stat-lbl">Session comparison</span>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-showcase" id="what-you-get">
        <div className="lp-showcase-intro lp-reveal">
          <h2>What happens after you upload a flight</h2>
          <p>
            Every replay gets broken down into views you can actually learn
            from.
          </p>
        </div>

        <div className="lp-feature">
          <div className="lp-feature-text lp-reveal-left">
            <span className="lp-feature-tag">Overview</span>
            <h2>The big picture, instantly</h2>
            <p>
              The moment your telemetry is processed, you land on a single page
              that tells the story of the flight. An AI-written summary at the
              top, key numbers on the right — distance, speed range, altitude
              band, G-load, battery drain. Below that, a smoothness score that
              tells you how steady you were, and the weather conditions you
              flew in, with your track drawn on a map.
            </p>
            <p className="lp-feature-note">
              Think of it as the flight's report card — one glance, and you
              know what happened.
            </p>
          </div>
          <div className="lp-feature-visual lp-reveal-right">
            <img
              src="/images/landing/overview.png"
              alt="Flight overview with AI summary, smoothness score, and weather"
              loading="lazy"
            />
          </div>
        </div>

        <div className="lp-feature lp-feature-reverse">
          <div className="lp-feature-text lp-reveal-right">
            <span className="lp-feature-tag lp-tag-accent">AI Insight</span>
            <h2>Honest feedback, backed by data</h2>
            <p>
              After every flight the AI reads through your entire telemetry and
              writes a debrief — what you did well and where you can improve.
              It doesn't sugarcoat: if you crashed at 11 seconds, it says so.
              If your rolls hit 93 degrees, it flags the instability risk.
            </p>
            <p className="lp-feature-note">
              Each point references specific numbers from your data, so the
              feedback feels concrete — not generic. Instructors can use it as
              a starting point for their own review.
            </p>
          </div>
          <div className="lp-feature-visual lp-reveal-left">
            <img
              src="/images/landing/ai-insight.png"
              alt="AI-generated strengths and areas for improvement"
              loading="lazy"
            />
          </div>
        </div>

        <div className="lp-feature">
          <div className="lp-feature-text lp-reveal-left">
            <span className="lp-feature-tag lp-tag-info">3D Replay</span>
            <h2>Watch the flight again — from any angle</h2>
            <p>
              The replay drops you into a full 3D environment built on Cesium
              with real terrain and satellite imagery. Follow the drone in
              chase cam, switch to free-look to study a tricky turn, or park
              the camera at a checkpoint and watch from there.
            </p>
            <p className="lp-feature-note">
              The HUD stays with you the whole time — speed, altitude, heading,
              pitch, roll — so you always know what the drone was doing at any
              given moment. Checkpoint gates show up on the course so you can
              see how close your line actually was.
            </p>
          </div>
          <div className="lp-feature-visual lp-reveal-right">
            <img
              src="/images/landing/replay.png"
              alt="3D flight replay with chase camera and HUD overlay"
              loading="lazy"
            />
          </div>
        </div>

        <div className="lp-feature lp-feature-reverse">
          <div className="lp-feature-text lp-reveal-right">
            <span className="lp-feature-tag lp-tag-danger">Expert View</span>
            <h2>For when you want all the numbers</h2>
            <p>
              Seven time-aligned charts — altitude, relative altitude, speed,
              horizontal speed, vertical rate, heading, and battery — all
              scrolling together. Click anywhere on a chart and the replay
              jumps to that exact moment.
            </p>
            <p className="lp-feature-note">
              Below the charts you'll find KPI cards: track length, speed
              envelope, altitude ceiling, climb vs. descent ratio, and hover
              time. There's also a G-meter tab and a weather tab if you want to
              dig even deeper.
            </p>
          </div>
          <div className="lp-feature-visual lp-reveal-left">
            <img
              src="/images/landing/expert-view.png"
              alt="Time-aligned telemetry charts with KPI cards"
              loading="lazy"
            />
          </div>
        </div>

        <div className="lp-feature">
          <div className="lp-feature-text lp-reveal-left">
            <span className="lp-feature-tag lp-tag-accent">Compare</span>
            <h2>Did you actually get better?</h2>
            <p>
              Pick two sessions — maybe last week vs. today — and see every
              metric side by side. Duration, distance, average and max speed,
              altitude, peak G, smoothness, battery spent. The delta column
              shows the difference, color-coded so improvements jump out in
              green and regressions show up in amber.
            </p>
            <p className="lp-feature-note">
              It's the fastest way to answer the question every pilot asks
              after practice: "Am I actually making progress?"
            </p>
          </div>
          <div className="lp-feature-visual lp-reveal-right">
            <img
              src="/images/landing/compare.png"
              alt="Side-by-side session comparison with color-coded deltas"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="lp-steps" id="how-it-works">
        <div className="lp-steps-header lp-reveal">
          <span className="lp-feature-tag">How it works</span>
          <h2>Three minutes from landing to learning</h2>
        </div>
        <div className="lp-steps-grid lp-reveal-stagger">
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">1</div>
            <h3>Drop in your replay</h3>
            <p>
              Upload a VBS4 replay or CSV export. The parser handles the rest —
              no formatting, no column mapping, just drag and drop.
            </p>
          </div>
          <div className="lp-step-line lp-reveal" />
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">2</div>
            <h3>Read, watch, explore</h3>
            <p>
              Open the overview for a quick summary, watch the 3D replay to see
              what happened, or dive into charts for the full picture.
            </p>
          </div>
          <div className="lp-step-line lp-reveal" />
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">3</div>
            <h3>Come back and compare</h3>
            <p>
              After your next session, stack the two flights side by side. See
              what improved, what didn't, and where to focus next.
            </p>
          </div>
        </div>
      </section>

      <section className="lp-roles" id="who-its-for">
        <div className="lp-roles-header lp-reveal">
          <h2>Whether you fly or teach — it fits</h2>
          <p>The same data, shaped for two different workflows.</p>
        </div>
        <div className="lp-roles-grid">
          <div className="lp-role-card lp-reveal-left">
            <div className="lp-role-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
              </svg>
            </div>
            <h3>I'm a pilot</h3>
            <p>
              You fly, upload, and get immediate feedback. Your dashboard
              tracks flights over time so you can see trends, not just
              individual sessions.
            </p>
            <ul className="lp-role-perks">
              <li>Personal dashboard with your flight history</li>
              <li>AI debrief after every upload</li>
              <li>Replay any flight in 3D whenever you want</li>
              <li>Compare sessions to spot your progress</li>
            </ul>
          </div>
          <div className="lp-role-card lp-role-card-highlight lp-reveal-right">
            <div className="lp-role-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3>I'm an instructor</h3>
            <p>
              You see all your students' flights in one place. The AI summary
              gives you a head start on the debrief, and the comparison view
              makes progress tangible.
            </p>
            <ul className="lp-role-perks">
              <li>All assigned pilot flights in one view</li>
              <li>Mark sessions as reviewed or flagged</li>
              <li>Use the AI analysis as a debrief starting point</li>
              <li>Show students their own improvement over time</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="lp-cta lp-reveal">
        <div className="lp-cta-glow" />
        <h2>Give it a try</h2>
        <p>
          Create an account, upload a replay, and see what your flight data
          actually looks like when it's laid out properly.
        </p>
        <div className="lp-hero-actions">
          <Link
            className="button primary lp-btn-lg"
            to="/register?next=/flights/upload"
          >
            Create account
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="18"
              height="18"
            >
              <path d="M5 12h14" />
              <path d="M12 5l7 7-7 7" />
            </svg>
          </Link>
          <Link className="button ghost lp-btn-lg" to="/login">
            Sign in
          </Link>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <img
              className="brand-mark"
              src="/logo.png"
              alt=""
              width={32}
              height={32}
              draggable={false}
            />
            <span>Flight Analytics</span>
          </div>
          <p className="lp-footer-copy">
            Drone replay intelligence for pilot training.
          </p>
        </div>
      </footer>
    </div>
  );
}
